import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loading from "../../../components/loading/Loading";
import MainContainer from "../../../components/container/MainContainer";
import { useNotification } from "../../../components/notification/NotificationContainer";
import { useEffect, useLayoutEffect } from "react";
import {
  addProposalBackground,
  getProject,
  unsetSelectedProject,
} from "../../../features/projectsSlice";
import InputField from "../../../components/form/InputField";
import PageTitle from "../../../components/titles/PageTitle";
import ArrayInputField from "../../../components/form/ArrayInputField";
import { zodResolver } from "@hookform/resolvers/zod";
import { addProposalBackgroundSchema } from "../../../schemas";
import EditorField from "../../../components/form/EditorField";
import loadStatus from "../../../util/loadStatus";
import NetworkError from "../../error/NetworkError";

const ProposalBackground = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const project = useSelector((state) => state.projects.selected);
  const projectLoad = useSelector((state) => state.projects.load.getProject);
  const addProposalLoad = useSelector(
    (state) => state.projects.load.addProposalBackground
  );
  const addNotification = useNotification();
  const {
    register,
    reset,
    setValue,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      activities: [{ id: null, number: 1, name: "" }],
      objectives: [{ id: null, number: 1, name: "" }],
      expected_results: [{ id: null, number: 1, name: "" }],
      justification: "",
      aim: "",
    },
    resolver: zodResolver(addProposalBackgroundSchema),
  });

  const onSubmit = async (data, e) => {
    e.preventDefault();
    try {
      const res = await dispatch(addProposalBackground(data));
      if (addProposalBackground.fulfilled.match(res)) {
        addNotification("Successfully saved!", "success");
        return navigate("/proposals/details/" + id);
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (error) {
      addNotification(error.message, "warning");
    }
  };

  useLayoutEffect(() => {
    dispatch(getProject(id));

    return () => {
      dispatch(unsetSelectedProject());
    };
  }, []);

  useEffect(() => {
    if (project.id) {
      reset({
        aim: project.aim,
        selected_indicators: project.selected_indicators,
        purpose: project.purpose,
        expected_results:
          project.expected_results.length > 0
            ? project.expected_results
            : [{ id: null, number: 1, name: "" }],
        activities:
          project.activities.length > 0
            ? project.activities
            : [{ id: null, number: 1, name: "" }],
        objectives:
          project.objectives.length > 0
            ? project.objectives
            : [{ id: null, number: 1, name: "" }],
      });
    }
  }, [project]);

  if (
    (id && project.id && project.stage.step_number < 8) ||
    (id && project.id && project.stage.step_number > 8)
  ) {
    return navigate("/requests/my-requests");
  }

  if (projectLoad === loadStatus.loading) {
    return <Loading />;
  }

  if (projectLoad === loadStatus.error) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <div className="w-full max-w-5xl mx-auto">
        <PageTitle title="Planning  - Proposal Background" />
        {project && (
          <form
            onSubmit={(e) => handleSubmit(onSubmit)(e)}
            method="post"
            encType="multipart/form-data"
          >
            <div className="text-text-1/70  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 font-roboto">
              <h5 className="text-text-1/80 ">Proposal Background</h5>
            </div>
            <div className="text-text-1/60  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 bg-content">
              {/*  */}

              <EditorField
                register={register}
                name="aim"
                label="Aim"
                errors={errors.aim}
                defaultValue={project.aim || ""}
                setValue={setValue}
                type="text"
              />
              <EditorField
                register={register}
                name="purpose"
                label="Purpose"
                errors={errors.purpose}
                defaultValue={project.purpose || ""}
                setValue={setValue}
                type="text"
              />

              <div>
                <ArrayInputField
                  register={register}
                  name="objectives"
                  control={control}
                  errors={errors.objectives}
                  label="Objectives"
                  setValue={setValue}
                  defaultValue={project.objectives}
                />
                <ArrayInputField
                  register={register}
                  name="expected_results"
                  control={control}
                  errors={errors.expected_results}
                  label="Results"
                  setValue={setValue}
                  defaultValue={project.expected_results}
                />
                <ArrayInputField
                  register={register}
                  name="activities"
                  control={control}
                  errors={errors.activities}
                  label="Planned Measures & Activities"
                  setValue={setValue}
                  defaultValue={project.activities}
                />
              </div>
              <EditorField
                register={register}
                name="selected_indicators"
                label="Selected Indicators"
                errors={errors.selected_indicators}
                defaultValue={project.selected_indicators || ""}
                setValue={setValue}
              />

              <InputField
                register={register}
                name="id"
                type="hidden"
                defaultValue={id}
              />
              <div className="flex justify-between items-center pt-4 pb-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/proposals/basics/" + project.id);
                  }}
                  className="cancel-rss-btn"
                  disabled={addProposalLoad}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={addProposalLoad}
                  className="add-rss-btn"
                >
                  Save & Continue
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </MainContainer>
  );
};

export default ProposalBackground;
